/**
 * 全部接口定义（前缀 https://ztc1349.com/api/）
 */
import { post } from './client';

// ==================== 用户 ====================

/** 登录返回 */
export interface LoginResult {
  token: string;
  expires_in: number;
  data: UserInfo;
}

/** 用户信息（含账户余额） */
export interface UserInfo {
  id: number;
  phone: string;
  phone_masked: string;
  nickname: string;
  avatar: string;
  invite_code: string;
  user_type: number;
  is_super_admin: number;
  realname_status: number;
  level_name: string;
  accounts: {
    bonus: number;
    balance: number;
    integral: number;
    coupon: number;
    promotion: number;
    voucher: number;
  };
}

/** 登录 */
export const login = (phone: string, password: string) =>
  post<LoginResult>('login', { phone, password });

/** 注册（成功直接登录） */
export const register = (phone: string, password: string, confirm_password: string, invite_code: string) =>
  post<LoginResult>('register', { phone, password, confirm_password, invite_code });

/** 发送短信验证码 */
export const sendCode = (phone: string) =>
  post<{ code: string }>('sendCode', { phone });

/** 忘记密码 */
export const forgotPassword = (phone: string, code: string, password: string, confirm_password: string) =>
  post<unknown>('forgotPassword', { phone, code, password, confirm_password });

/** 个人中心 */
export interface ProfileData {
  user: UserInfo;
  buyerOrders: { label: string; count: number }[];
  sellerOrders: { label: string; count: number }[];
  assets: { label: string; amount: number; subLabel?: string }[];
}

export const getUserInfo = () => post<ProfileData>('user/info');

/** 修改个人资料（头像 base64 可选，仅传需要修改的字段也可只改昵称） */
export const updateProfile = (nickname: string, avatar = '') =>
  post<{ nickname: string; avatar: string }>('user/updateProfile', { nickname, avatar });

/** 退出登录 */
export const logout = () => post<unknown>('user/logout');

// ==================== 首页/公告 ====================

export interface HomeData {
  nickname: string;
  banners: { id: number; image: string; title: string; subtitle: string; tag: string }[];
  announcements: AnnouncementItem[];
  featuredProduct: FeaturedProduct | null;
  voucherProducts: ExchangeMallProduct[];
}

export interface AnnouncementItem {
  id: number;
  type: string;
  title: string;
  date: string;
  fullDate: string;
  content: string;
  detailContent?: string;
}

export interface FeaturedProduct {
  id: number;
  image: string;
  name: string;
  subtitle: string;
  description: string;
  tag: string;
  price: number;
  originalPrice: number;
  unit: string;
}

export interface ExchangeMallProduct {
  id: number;
  image: string;
  name: string;
  points: number;
  originalPrice: number;
}

export const getHome = () => post<HomeData>('index');

//代金券兑换商城列表（分页）
export const getExchangeMall = (page = 1, limit = 10) =>
  post<{ count: number; list: ExchangeMallProduct[] }>('exchangeMall', { page, limit });

//兑换商品详情
export interface ExchangeDetail {
  id: number;
  name: string;
  images: string[];
  description: string;
  price: number;
  points: number;
  voucherBalance: number;
}

export const getExchangeDetail = (id: number) =>
  post<ExchangeDetail>('exchangeDetail', { id });

//兑换提交（代金券储备扣款）
export const submitExchange = (id: number, addressId: number) =>
  post<unknown>('exchangeSubmit', { id, address_id: addressId });

//平台协议（签约中心，含富文本内容）
export interface AgreementItem {
  id: number;
  name: string;
  content: string;
}

export const getAgreements = () => post<AgreementItem[]>('agreements');

export const getAnnouncements = (page = 1, limit = 20) =>
  post<{ count: number; list: AnnouncementItem[] }>('announcements', { page, limit });

export const getAnnouncementDetail = (id: number) =>
  post<AnnouncementItem & { detailContent: string }>('announcements/detail', { id });

// ==================== 实名认证 ====================

export interface KycInfo {
  realnameStatus: number; // 0未认证 1待审核 2已认证 3已拒绝
  realName: string;
  idCard: string;
  rejectReason: string;
  status: number | null; // 0待审核 1通过 2拒绝
}

export const getKycInfo = () => post<KycInfo>('kyc/info');

export const submitKyc = (frontImage: string, backImage: string, realName: string, idCard: string) =>
  post<unknown>('kyc/submit', { frontImage, backImage, realName, idCard });

// ==================== 收货地址 ====================

export interface Address {
  id: string;
  name: string;
  phone: string;
  province: string;
  city: string;
  district: string;
  detail: string;
  isDefault: boolean;
}

export const getAddresses = () => post<Address[]>('address/lists');

export const saveAddress = (data: {
  id?: number;
  name: string;
  phone: string;
  province: string;
  city: string;
  district: string;
  detail: string;
  isDefault: number;
}) => post<unknown>('address/post', data);

export const setDefaultAddress = (id: number) => post<unknown>('address/default', { id });

export const deleteAddress = (id: number) => post<unknown>('address/del', { id });

// ==================== 订单 ====================

export interface BuyerOrder {
  id: string;
  orderNo: string;
  status: string;
  productName: string;
  productImage: string;
  price: number;
  sellerName: string;
  sellerPhone: string;
  buyerName: string;
  buyerPhone: string;
  purchaseTime: string;
  paymentImage: string;
  paymentStatus: string;
  isExchange?: boolean; //兑换订单（无签名环节，隐藏签名区）
  finalAmount: number;
}

export interface SellerOrder {
  id: string;
  orderNo: string;
  status: string;
  productName: string;
  productImage: string;
  price: number;
  buyerName: string;
  buyerPhone: string;
  paymentImage: string;
  paymentStatus: string;
  purchaseDate: string;
  canConfirm: boolean;
  isConfirmed: boolean;
  finalAmount: number;
}

export const getBuyerOrders = (status = 0, page = 1, limit = 20) =>
  post<{ count: number; list: BuyerOrder[]; rates: { consignment: number; storage: number }; isBoss?: boolean }>('orders/buyer', { status, page, limit });

/** 挂卖：可挂卖订单转手重新挂拍，生成新挂卖单 */
export const listingOrder = (id: number) =>
  post<{ listing_price: number }>('orders/listing', { id });

export const getSellerOrders = (status = 0, page = 1, limit = 20) =>
  post<{ count: number; list: SellerOrder[] }>('orders/seller', { status, page, limit });

/** 买家上传付款凭证（base64 图片） */
export const uploadVoucher = (id: number, image: string, remark = '') =>
  post<unknown>('orders/uploadVoucher', { id, image, remark });

/** 卖家确认收款 */
export const confirmOrder = (id: number) => post<unknown>('orders/confirm', { id });

/** 订单转赠：可挂卖订单按手机号转赠给其他用户 */
export const giftOrder = (id: number, phone: string) =>
  post<unknown>('orders/gift', { id, phone });

// ==================== 资金账户明细 ====================

export interface AccountRecord {
  id: string;
  type: string;
  amount: number;
  direction?: number;
  remark?: string;
  buyerName?: string;
  orderNumber?: string;
  tradeAmount?: number;
  date: string;
  source?: string;
  description?: string;
  expireAt?: string;
  sellerName?: string;
  sellerPhone?: string;
  buyerPhone?: string;
}

export interface AccountDetails {
  balance: number;
  income: number;
  expense: number;
  totalIncome?: number;
  totalExpense?: number;
  count: number;
  records: AccountRecord[];
  rules?: string[];
}

/** 代金券储备明细 */
export const getVoucherDetails = (page = 1, limit = 20) =>
  post<AccountDetails>('account/voucher', { page, limit });

/** 优惠券明细 */
export const getCouponDetails = (page = 1, limit = 20) =>
  post<AccountDetails>('account/coupon', { page, limit });

/** 我的奖金明细（早市激励） */
export const getBonusDetails = (page = 1, limit = 20) =>
  post<AccountDetails>('account/bonus', { page, limit });

/** 推广佣金明细 */
export const getCommissionDetails = (page = 1, limit = 20) =>
  post<AccountDetails>('account/commission', { page, limit });

// ==================== 分销团队 ====================

export interface TeamData {
  teamTotal: number;
  directMembers: number;
  memberCount: number;
  leaderCount: number;
  leaderCondition: { requirement: number; current: number; depth: number };
  count: number;
  members: { id: string; name: string; phone: string; joinDate: string; level: string }[];
}

export const getTeam = () => post<TeamData>('team');

export interface InviteData {
  userName: string;
  inviteCode: string;
  platformName: string;
  slogan: string;
  inviteTitle: string;
  inviteDesc: string;
  rules: string[];
}

export const getInvite = () => post<InviteData>('invite');

// ==================== 进货市场抢购 ====================

export interface RushSlot {
  id: number;
  name: string;
  startTime: string;
  endTime: string;
  state: number; // 1进行中 2已结束 3未开始
}

export interface RushData {
  slots: RushSlot[];
  currentSlotId: number;
  nextStartAt: number;
  serverTime: number;
  todayRecords: {
    id: string;
    orderNo: string;
    productName: string;
    productImage: string;
    amount: number;
    status: number;
    statusText: string;
    time: string;
  }[];
  quota: { morningQuota: number; morningCount: number; userType: number; unlimited?: boolean };
  listings: { id: number; name: string; image: string; price: number; sellerName: string; sellerPhone: string }[];
}

export const getRush = () => post<RushData>('rush');

/** 抢单提交（抢挂卖单） */
export const submitRush = (orderId: number, slotId: number) =>
  post<{ orderId: number; orderNo: string; amount: number }>('rush/submit', { orderId, slotId });

// ==================== 公共配置 ====================

/** 公共配置（按 key 读取，协议/隐私政策为后台富文本 content） */
export const getSetting = (key: string) =>
  post<{ setting: { content?: string } }>('setting', { key });
